import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ArrowLeft, Database, Plus, Trash2, ChevronUp, ChevronDown, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  CmsCollectionService,
  CMS_FIELD_TYPES,
  CMS_FIELD_TYPE_LABELS,
  type CmsFieldDefinition,
  type CmsFieldType,
} from "@/services/cms";

function slugifyKey(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

let fieldIdCounter = 0;
function nextFieldId() {
  fieldIdCounter += 1;
  return `f${fieldIdCounter}`;
}

interface EditableField extends CmsFieldDefinition {
  _id: string;
  _optionsText?: string;
}

const CmsCollectionBuilder: React.FC = () => {
  const [location, setLocation] = useLocation();
  const id = location.includes("/cms/collections/") ? location.split("/cms/collections/")[1]?.split("/edit")[0] : null;
  const isEdit = !!id && id !== "new";

  const [key, setKey] = useState("");
  const [keyEditedManually, setKeyEditedManually] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [hasSlug, setHasSlug] = useState(true);
  const [fields, setFields] = useState<EditableField[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEdit && id) {
      load(id);
    }
  }, [id]);

  const load = async (collectionId: string) => {
    setIsLoading(true);
    setError("");
    try {
      const collection = await CmsCollectionService.get(collectionId);
      setKey(collection.key);
      setKeyEditedManually(true);
      setName(collection.name);
      setDescription(collection.description || "");
      setHasSlug(collection.hasSlug);
      setFields(collection.fields.map((f) => ({ ...f, _id: nextFieldId(), _optionsText: (f.options || []).join(", ") })));
    } catch (err: any) {
      console.error("Failed to load collection:", err);
      setError("Failed to load collection. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (!keyEditedManually) setKey(slugifyKey(value));
  };

  const addField = () => {
    setFields((prev) => [...prev, { _id: nextFieldId(), key: "", label: "", type: "text", required: false }]);
  };

  const removeField = (fieldId: string) => {
    setFields((prev) => prev.filter((f) => f._id !== fieldId));
  };

  const moveField = (fieldId: string, direction: -1 | 1) => {
    setFields((prev) => {
      const index = prev.findIndex((f) => f._id === fieldId);
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const updateField = (fieldId: string, patch: Partial<EditableField>) => {
    setFields((prev) => prev.map((f) => (f._id === fieldId ? { ...f, ...patch } : f)));
  };

  const handleFieldLabelChange = (fieldId: string, label: string) => {
    setFields((prev) =>
      prev.map((f) => {
        if (f._id !== fieldId) return f;
        const autoKey = f.key === "" || f.key === slugifyKey(f.label);
        return { ...f, label, key: autoKey ? slugifyKey(label) : f.key };
      })
    );
  };

  const handleSave = async () => {
    setError("");

    if (!name.trim()) return setError("Collection name is required.");
    if (!key.trim()) return setError("Collection key is required.");
    if (fields.length === 0) return setError("Add at least one field.");

    for (const field of fields) {
      if (!field.label.trim()) return setError("Every field needs a label.");
      if (!field.key.trim()) return setError(`Field "${field.label}" needs a key.`);
      if (field.type === "select" && !(field._optionsText || "").trim()) {
        return setError(`Field "${field.label}" is an Option field — add at least one option.`);
      }
    }

    const keys = fields.map((f) => f.key);
    if (new Set(keys).size !== keys.length) {
      return setError("Field keys must be unique.");
    }

    setIsSaving(true);
    try {
      const payloadFields: CmsFieldDefinition[] = fields.map((f) => ({
        key: f.key,
        label: f.label,
        type: f.type,
        required: f.required || undefined,
        options:
          f.type === "select"
            ? (f._optionsText || "").split(",").map((o) => o.trim()).filter(Boolean)
            : undefined,
        helpText: f.helpText || undefined,
      }));

      if (isEdit && id) {
        await CmsCollectionService.update(id, { name, description: description || undefined, fields: payloadFields, hasSlug });
      } else {
        await CmsCollectionService.create({ key, name, description: description || undefined, fields: payloadFields, hasSlug });
      }
      setLocation("/cms");
    } catch (err: any) {
      setError(err?.message || "Failed to save collection.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-gray-600">Loading collection...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setLocation("/cms")}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <Database className="h-8 w-8 text-primary shrink-0" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{isEdit ? "Edit Collection" : "New Collection"}</h1>
              <p className="text-gray-600">Define the fields every item in this collection will have.</p>
            </div>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={cn(
            "flex items-center justify-center space-x-2 px-6 py-2 text-sm font-medium rounded-md transition-colors w-full sm:w-auto",
            isSaving ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-primary text-white hover:bg-primary/90"
          )}
        >
          <Save className="h-4 w-4" />
          <span>{isSaving ? "Saving..." : isEdit ? "Save Changes" : "Create Collection"}</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Collection Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Blog Posts, Testimonials, FAQs"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Key *</label>
            <input
              type="text"
              value={key}
              disabled={isEdit}
              onChange={(e) => {
                setKeyEditedManually(true);
                setKey(slugifyKey(e.target.value));
              }}
              placeholder="blog_posts"
              className={cn(
                "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary",
                isEdit && "bg-gray-50 text-gray-500"
              )}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isEdit ? "Can't change after creation." : "Used in API routes — lowercase, underscores only."}
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
            placeholder="What is this collection for?"
          />
        </div>

        <div className="flex items-center">
          <input
            id="hasSlug"
            type="checkbox"
            checked={hasSlug}
            onChange={(e) => setHasSlug(e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="hasSlug" className="ml-2 block text-sm text-gray-700">
            Items have a URL slug
          </label>
          <span className="ml-2 text-xs text-gray-400">(needed for a page like /blogs/my-post)</span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Fields</h2>
          <button
            onClick={addField}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/5 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add Field</span>
          </button>
        </div>

        {fields.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No fields yet — add the first one above.</p>
        ) : (
          <div className="space-y-3">
            {fields.map((field, index) => (
              <div key={field._id} className="border border-gray-200 rounded-md p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="flex sm:flex-col gap-1 shrink-0 order-2 sm:order-1">
                    <button
                      onClick={() => moveField(field._id, -1)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Move field up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => moveField(field._id, 1)}
                      disabled={index === fields.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Move field down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 order-1 sm:order-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => handleFieldLabelChange(field._id, e.target.value)}
                        placeholder="Title"
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Key</label>
                      <input
                        type="text"
                        value={field.key}
                        onChange={(e) => updateField(field._id, { key: slugifyKey(e.target.value) })}
                        placeholder="title"
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary focus:border-primary font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                      <select
                        value={field.type}
                        onChange={(e) => updateField(field._id, { type: e.target.value as CmsFieldType })}
                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      >
                        {CMS_FIELD_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {CMS_FIELD_TYPE_LABELS[t]}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm text-gray-700 pb-1.5">
                        <input
                          type="checkbox"
                          checked={!!field.required}
                          onChange={(e) => updateField(field._id, { required: e.target.checked })}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        Required
                      </label>
                    </div>
                    {field.type === "select" && (
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Options (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={field._optionsText || ""}
                          onChange={(e) => updateField(field._id, { _optionsText: e.target.value })}
                          placeholder="MBA Corner, Career Planning & Guidance, ..."
                          className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                        />
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => removeField(field._id)}
                    className="text-gray-400 hover:text-red-600 transition-colors shrink-0 order-3 self-start"
                    aria-label="Remove field"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CmsCollectionBuilder;
